import { CreateVoucher, UpdateVoucher, VouchersApiFactory } from '@flipdish/api-client-typescript';
import axios from 'axios';

import {
  type ApplyVoucherDateBoundariesOptions,
  applyVoucherDateBoundaries,
} from '../components/Vouchers/utils/voucherUtils';
import { getApiEndPoint } from '../helpers/apibase';
import { store } from '../helpers/store';
import { createApi } from '../helpers/utilities';
import { mapServerError } from './utils/serverErrorMapper';

const vouchersApi = createApi(VouchersApiFactory);

// TODO - remove when expiryUsesStoreTimezone is fully released
export type VoucherDateOptions = Pick<
  ApplyVoucherDateBoundariesOptions,
  'expiryUsesStoreTimezoneLegacyDayBoundaries'
>;

// Voucher validity dates are converted here, on the way out, mirroring the parsing that getById does
// on the way in — so no caller has to think about timezones. This is also why the bulk importer, which
// calls create() directly rather than going through the thunk, is covered.
function withVoucherDates<
  T extends { StartDate?: any; ExpiryDate?: any; Stores?: number[]; TimeZone?: string },
>(voucher: T, options: ApplyVoucherDateBoundariesOptions = {}): T {
  const converted = applyVoucherDateBoundaries(voucher, store.getState(), options);
  // TimeZone is form-only: it tells us which zone to compute the boundaries in, and isn't part of the
  // voucher contract.
  delete converted.TimeZone;
  return converted;
}

const baseURL = getApiEndPoint();

export const voucherUpdateApi = axios.create({
  baseURL: `${baseURL}/api/v1.0`,
  withCredentials: true,
});

export const vouchersService = {
  getVouchers,
  getVoucher,
  getVoucherStatistics,
  updateVoucher,
  create,
  getCustomerVouchers,
};

function getVouchers(
  appId: string,
  pageIndex?: number,
  pageSize?: number,
  searchCodes?: string[],
  statusSearch?: Array<'Valid' | 'Expired' | 'Used'>,
  typeSearch?: Array<'PercentageDiscount' | 'LumpDiscount' | 'AddItem' | 'CreditNote'>,
  subTypeSearch?: Array<
    'None' | 'SignUp' | 'Loyalty' | 'Loyalty25' | 'Retention' | 'SecondaryRetention' | 'Custom'
  >,
  storeIds?: number[],
  channelRestrictions?: Array<'Ios' | 'Android' | 'Web' | 'Kiosk' | 'Pos' | 'Google'>
) {
  return vouchersApi
    .getVouchers(
      appId,
      pageIndex,
      pageSize,
      searchCodes,
      statusSearch,
      typeSearch,
      subTypeSearch,
      storeIds,
      channelRestrictions
    )
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

function getVoucher(voucherId: number) {
  return vouchersApi
    .getVoucherById(voucherId)
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

async function updateVoucher(
  voucherId: number,
  voucher: UpdateVoucher,
  percentDiscountDetails?: { percentValue: number; maxDiscountAmount: number },
  lumpValue?: number,
  dateOptions: VoucherDateOptions = {}
) {
  try {
    // Guarded on VoucherId so a previously-viewed voucher still in state isn't mistaken for this
    // one's original.
    const loadedVoucher = store.getState().vouchers?.voucher;
    const originalVoucher =
      loadedVoucher && loadedVoucher.VoucherId === voucherId ? loadedVoucher : undefined;
    const body = withVoucherDates(voucher, { ...dateOptions, originalVoucher });

    let queryString = '';

    if (percentDiscountDetails || lumpValue) {
      queryString = percentDiscountDetails
        ? `/?percentValue=${percentDiscountDetails.percentValue}&maxDiscountAmount=${percentDiscountDetails.maxDiscountAmount}`
        : `/?lumpValue=${lumpValue}`;
    }

    const incomingMessage = await voucherUpdateApi.post(
      `vouchers/${voucherId}${queryString}`,
      body
    );

    return incomingMessage.data.Data;
  } catch (incomingMessage) {
    const err = await mapServerError(incomingMessage);
    throw err;
  }
}

function getVoucherStatistics(voucherId: number) {
  return vouchersApi
    .getVoucherStatsById(voucherId, 'Daily')
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

async function create(appId: string, voucher: CreateVoucher, dateOptions: VoucherDateOptions = {}) {
  try {
    const incomingMessage = await vouchersApi.createVoucher(
      appId,
      withVoucherDates(voucher, dateOptions)
    );
    return incomingMessage.Data;
  } catch (incomingMessage) {
    const err = await mapServerError(incomingMessage);
    throw err;
  }
}

export type AssignedVoucherSummaryResponse = {
  Data: AssignedVoucherSummary[];
};

export type AssignedVoucherSummary = {
  VoucherId: number;
  Code: string | null;
  Status: 'Valid' | 'NotYetValid' | 'Expired' | 'Used' | 'Disabled' | string;
  VoucherType: string;
  VoucherSubType?: string;
  Description?: string | null;
  IsEnabled?: boolean;
};

async function getCustomerVouchers(
  appId: string,
  customerId: number
): Promise<AssignedVoucherSummary[]> {
  const response = await voucherUpdateApi.get<AssignedVoucherSummaryResponse>(
    `vouchers/${appId}/customer/${customerId}`
  );
  return response?.data?.Data ?? [];
}
